import React, { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import {
  FiX,
  FiTrendingUp,
  FiUsers,
  FiUpload,
  FiExternalLink,
  FiColumns,
  FiCode,
  FiLink2,
} from 'react-icons/fi';
import { Trans, useTranslation } from 'react-i18next';
import { fetchDCOUrl, openUrlByToken } from 'domains/user/reduser';
import { useProjectInfo } from 'domains/project/hooks';
import { isDev } from 'utils/isDev';
import i18n from 'i18n';
import { useIsGroup } from 'domains/user/hooks';
import { ReactComponent as AgencySvg } from './icons/agency.svg';
import { ReactComponent as CampaingSvg } from './icons/campaing.svg';
import { ReactComponent as ReportSvg } from './icons/report.svg';
import { ReactComponent as ForecasterSvg } from './icons/forecaster.svg';
import { ReactComponent as ReportBuilderSvg } from './icons/reportBuilder.svg';
import './styles.scss';
import Dropdown from './Dropdown';
import { everyGroups, someGroups } from '../../utils/statics/everyGroups';
import { useModalBwListUpdate } from '../../domains/bw_lists_update/modal/hooks/useModal';
import { useUserInfo, useUserTheme } from '../../domains/user/model/selectors';
import { ReactComponent as OrdSvg } from './icons/ord.svg';
import { fetchSendCreoToGrid, fetchUploadDsp } from './reducer';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type TMenuItem = {
  type: 'item';
  id: string;
  visible: boolean | null;
  route: string;
  icon: ReactNode | null;
  label: ReactNode;
};

type TMenuDropdownItem = {
  type: 'dropdown';
  id: string;
  visible: boolean | null;
  menuIcon: JSX.Element;
  menuText: JSX.Element;
  showSelectedItem: boolean;
  children: (TMenuItem | TMenuAcrionItem)[];
};

type TMenuAcrionItem = {
  type: 'action';
  id: string;
  visible: boolean | null;
  onClick: (e: any) => void;
  href?: string;
  content: ReactNode;
};

type TMenu = TMenuItem | TMenuDropdownItem | TMenuAcrionItem;

const SideMenu: FC<Props> = ({ isOpen, onClose }) => {
  const { getValueFromSettingForKey } = useUserTheme();
  const { openModal } = useModalBwListUpdate();
  const history = useHistory();
  const location = useLocation();
  const { data: user } = useUserInfo();
  const { data: project } = useProjectInfo();
  const { isBWListsUpdate, isSelfPretargetingClient } = useIsGroup();

  const dispatch = useDispatch();
  const onClick = (to: string) => {
    history.push(to);
    onClose();
  };

  const { t } = useTranslation();

  const openLoad = useCallback(() => {
    dispatch(openUrlByToken('http://import.dashboard.terratraf.io/auth/?key='));
  }, [localStorage.getItem('$token'), user, location]);

  const dspLoadingHandler = () => {
    dispatch(fetchUploadDsp());
  };

  const sendCreoToGridHandler = () => {
    dispatch(fetchSendCreoToGrid());
  };

  const panelSupTitle = () => {
    if (location.pathname.includes('partners')) {
      return t('top.menu.partners');
    }
    if (location.pathname.includes('project')) {
      return t(`top.menu.project`);
    }
    if (location.pathname.includes('users')) {
      return t('top.menu.users');
    }
    if (location.pathname.includes('users_api')) {
      return t(`users_page.users_api`);
    }
    if (location.pathname.includes('history')) {
      return t('top.menu.history_list');
    }
    if (location.pathname.includes('analysis')) {
      return t('analysis_page.title');
    }
    if (location.pathname.includes('contracts')) {
      return t(`ord.contracts_title`);
    }
    if (location.pathname.includes('counteragents')) {
      return t(`ord.сounterparties_title`);
    }
    if (location.pathname.includes('blackLists')) {
      return t(`blackLists.title`);
    }
    return '';
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'initial';
    }
  }, [isOpen]);

  const menuItems: TMenu[] = useMemo(
    () => [
      {
        type: 'item',
        id: 'agencies',
        visible: user && user.partner.type === 'ORGANIZATION',
        route: '/main/agencies',
        icon: <AgencySvg className="menu__icon" />,
        label: t('top.menu.agency'),
      },
      {
        type: 'item',
        id: 'clients',
        visible:
          (user && user.partner.type === 'AGENCY') ||
          (user && user.partner.type === 'ORGANIZATION'),
        route: '/main/clients',
        icon: <FiUsers size={24} />,
        label: t('top.menu.clients'),
      },
      {
        type: 'item',
        id: 'campaigns',
        visible: user && someGroups(user, 'default', 'owner', 'self_user'),
        route: '/main/campaigns',
        icon: <CampaingSvg className="svg" />,
        label: t('top.menu.campaigns'),
      },
      {
        type: 'item',
        id: 'reports',
        visible:
          user &&
          user.partner.type === 'CLIENT' &&
          everyGroups(user, 'reports_client'),
        route: `/main/client/${user?.partner.xxhash}/reports`,
        icon: <ReportSvg className="svg" />,
        label: t(`clients_page.client_report`),
      },
      {
        type: 'item',
        id: 'reports_ag',
        visible:
          user &&
          user.partner.type === 'AGENCY' &&
          everyGroups(user, 'reports_client'),
        route: `/main/agency/${user?.partner.xxhash}/reports_ag`,
        icon: <ReportSvg className="svg" />,
        label: t(`agencies_page.agencies_report`),
      },
      {
        type: 'item',
        id: 'reports_au',
        visible:
          user &&
          user.partner.type === 'AGENCY' &&
          everyGroups(user, 'reports_client'),
        route: `/main/agency/${user?.partner.xxhash}/reports_au`,
        icon: <ReportSvg className="svg" />,
        label: t(`agencies_page.report.title`),
      },
      {
        type: 'item',
        id: 'report_builder',
        visible: everyGroups(user, 'owner'),
        route: `/main/report_builder`,
        icon: <ReportBuilderSvg className="svg" />,
        label: t(`reportbuilder.title`),
      },
      {
        type: 'action',
        id: 'forecaster',
        visible: everyGroups(user, 'owner'),
        onClick: (event: any) => {
          event.preventDefault();
        },
        href: 'https://forecast.mediasniper.ru/',
        content: (
          <>
            <ForecasterSvg className="svg" />
            <span>{t(`forecaster_title`)}</span>
          </>
        ),
      },
      {
        type: 'dropdown',
        id: 'ord',
        visible:
          (someGroups(user, 'owner', 'self_user') &&
            (user?.partner.type === 'AGENCY' ||
              user?.partner.type === 'ORGANIZATION') &&
            project?.ord_registration) ||
          false,
        menuIcon: <OrdSvg />,
        menuText: <span>{t(`ord.title`)}</span>,
        showSelectedItem:
          location.pathname === '/main/counteragents' ||
          location.pathname === '/main/contracts',
        children: [
          {
            type: 'item',
            id: 'counteragents',
            visible: true,
            route: '/main/counteragents',
            icon: null,
            label: t(`ord.сounterparties_title`),
          },
          {
            type: 'item',
            id: 'contracts',
            visible: true,
            route: '/main/contracts',
            icon: null,
            label: t(`ord.contracts_title`),
          },
        ],
      },
      {
        type: 'item',
        id: 'offers',
        visible:
          user &&
          !getValueFromSettingForKey('offer') &&
          getValueFromSettingForKey('offer') !== null,
        route: '/main/offers',
        icon: <FiLink2 size={24} />,
        label: t(`offers_page.offers`),
      },
      {
        type: 'item',
        id: 'containers',
        visible: user && everyGroups(user, 'container'),
        route: '/main/containers',
        icon: <FiTrendingUp size={24} />,
        label: t('top.menu.containers'),
      },
      {
        type: 'action',
        id: 'DCO',
        visible: everyGroups(user, 'dco_viewer'),
        onClick: (event: any) => {
          event.preventDefault();
          dispatch(fetchDCOUrl());
        },
        href: 'http://uploads.mediasniper.ru/getjson/',
        content: (
          <>
            <FiCode size={24} />
            <span>DCO</span>
            <FiExternalLink size={16} />
          </>
        ),
      },
      {
        type: 'action',
        id: 'LoadData',
        visible:
          process.env.REACT_APP_API !== 'https://tdm5.adcloud.org/api/' &&
          user &&
          user?.partner.type !== 'CLIENT' &&
          everyGroups(user, 'owner'),
        onClick: openLoad,
        content: (
          <>
            <FiUpload size={24} />
            <span>{t('top.menu.load_data')}</span>
            <FiExternalLink size={16} />
          </>
        ),
      },
      {
        type: 'action',
        id: 'uploadbwl',
        visible: isSelfPretargetingClient,
        onClick: () => {
          openModal();
        },
        content: t('top.menu.uploadbwl'),
      },
      {
        type: 'dropdown',
        id: 'control_panel',
        visible: everyGroups(user, 'manager'),
        menuIcon: <FiColumns size={24} />,
        menuText: <span>{t('top.menu.control_panel')}</span>,
        showSelectedItem:
          location.pathname.includes('project') ||
          location.pathname.includes('partners') ||
          location.pathname.includes('users') ||
          location.pathname.includes('users_api') ||
          location.pathname.includes('history') ||
          location.pathname.includes('analysis') ||
          location.pathname.includes('blackLists'),
        children: [
          {
            type: 'item',
            id: 'project',
            visible: everyGroups(user, 'owner'),
            route: '/control_panel/project',
            icon: null,
            label: t('top.menu.project'),
          },
          {
            type: 'item',
            id: 'partners',
            visible: true,
            route: '/control_panel/partners',
            icon: null,
            label: t('top.menu.partners'),
          },
          {
            type: 'item',
            id: 'users',
            visible: true,
            route: '/control_panel/users',
            icon: null,
            label: t('top.menu.users'),
          },
          {
            type: 'item',
            id: 'users_api',
            visible: everyGroups(user, 'owner', 'manager'),
            route: '/control_panel/users_api',
            icon: null,
            label: t(`users_page.users_api`),
          },
          {
            type: 'item',
            id: 'history_changes',
            visible: everyGroups(user, 'manager', 'owner'),
            route: '/control_panel/history_changes',
            icon: null,
            label: t('top.menu.history_list'),
          },
          {
            type: 'item',
            id: 'analysis',
            visible: everyGroups(user, 'owner', 'manager'),
            route: '/control_panel/analysis',
            icon: null,
            label: t('analysis_page.title'),
          },
          {
            type: 'item',
            id: 'tasks',
            visible: isDev() && everyGroups(user, 'test', 'owner'),
            route: '/control_panel/tasks',
            icon: null,
            label: (
              <Trans
                i18nKey="bulkCreativeUploadWrap"
                components={{ br: <br /> }}
              />
            ),
          },
          {
            type: 'action',
            id: 'uploadbwl',
            visible: isBWListsUpdate,
            onClick: () => {
              openModal();
            },
            content: t('top.menu.uploadbwl'),
          },
          {
            type: 'item',
            id: 'blackLists',
            visible: everyGroups(user, 'owner', 'manager'),
            route: '/control_panel/blackLists',
            icon: null,
            label: t(`blackLists.title`),
          },
          {
            type: 'action',
            id: 'update_file_dsp',
            visible: isDev() && everyGroups(user, 'test', 'owner'),
            onClick: dspLoadingHandler,
            content: t(`translations.update_file_dsp`),
          },
          {
            type: 'action',
            id: 'send_creo_to_grid',
            visible: isDev() && everyGroups(user, 'test', 'owner'),
            onClick: sendCreoToGridHandler,
            content: t(`translations.send_creo_to_grid`),
          },
        ],
      },
    ],
    [i18n.language, location.pathname, user],
  );

  return (
    <div>
      <div className={cn('side-menu', { isOpened: isOpen })}>
        <div className="side-menu__bg" onClick={onClose} />
        <div className="side-menu-overflow">
          <div className="side-menu__card">
            <div className="header">
              <div className="header__close">
                <FiX size={24} onClick={onClose} />
              </div>
            </div>
            <div className="menu">
              {menuItems
                .filter((item) => item.visible)
                .map((item) => {
                  if (item.type === 'item') {
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onClick(item.route)}
                        className={cn('menu__item', {
                          isActive: location.pathname === item.route,
                        })}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </button>
                    );
                  }
                  if (item.type === 'dropdown') {
                    return (
                      <Dropdown
                        menuIcon={item.menuIcon}
                        menuText={item.menuText}
                        selectedItem={panelSupTitle()}
                        showSelectedItem={item.showSelectedItem}
                        toggleSide={isOpen}
                        key={item.id}
                      >
                        {item.children
                          .filter((child) => child.visible)
                          .map((ch) => {
                            if (ch.type === 'action') {
                              return (
                                <button
                                  type="button"
                                  onClick={ch.onClick}
                                  key={ch.id}
                                >
                                  <span>{ch.content}</span>
                                </button>
                              );
                            }

                            return (
                              <button
                                key={ch.id}
                                type="button"
                                onClick={() => onClick(ch.route)}
                                className={cn({
                                  isActive: location.pathname === ch.route,
                                })}
                              >
                                {ch.icon}
                                <span>{ch.label}</span>
                              </button>
                            );
                          })}
                      </Dropdown>
                    );
                  }
                  if (item.type === 'action') {
                    // Если есть href — это внешняя ссылка.
                    if (item.href) {
                      return (
                        <a
                          href={item.href}
                          className="menu__link"
                          target="_blank"
                          rel="noopener noreferrer"
                          key={item.id}
                        >
                          {item.content}
                        </a>
                      );
                    }

                    // Если href нет — это внутреннее действие.
                    return (
                      <button
                        type="button"
                        onClick={item.onClick}
                        key={item.id}
                        className="menu__link"
                      >
                        {item.content}
                      </button>
                    );
                  }
                  return null;
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideMenu;
